import type { FunnelStage } from './FunnelStageBadge';
import type { Application } from '../types';
import { FunnelCard } from './FunnelCard';

interface StageColumnProps {
  stage: FunnelStage;
  applications: Application[];
  jobId: number;
  onOpen: (applicationId: number) => void;
}

const stageStyle: Record<FunnelStage, { dot: string; label: string }> = {
  lead:      { dot: 'bg-stone-400',  label: 'text-stone-600  dark:text-stone-400' },
  screening: { dot: 'bg-sky-500',    label: 'text-sky-600    dark:text-sky-400' },
  interview: { dot: 'bg-amber-400',  label: 'text-amber-800  dark:text-amber-300' },
  offer:     { dot: 'bg-purple-400', label: 'text-purple-700 dark:text-purple-300' },
  placed:    { dot: 'bg-emerald-400',label: 'text-emerald-700 dark:text-emerald-300' },
  rejected:  { dot: 'bg-rose-400',   label: 'text-rose-700   dark:text-rose-400' },
  withdrawn: { dot: 'bg-stone-400',  label: 'text-stone-500  dark:text-gray-400' },
};

const MAX_VISIBLE = 20;

export function StageColumn({ stage, applications, jobId, onOpen }: StageColumnProps) {
  const { dot, label } = stageStyle[stage];

  const sorted = [...applications].sort((a, b) => {
    if (a.score == null && b.score == null) return 0;
    if (a.score == null) return 1;
    if (b.score == null) return -1;
    return b.score - a.score;
  });

  const visible = sorted.slice(0, MAX_VISIBLE);
  const overflow = sorted.length - MAX_VISIBLE;

  return (
    <div className="min-w-[220px] flex-shrink-0 bg-stone-50 dark:bg-gray-900 border border-stone-200 dark:border-gray-700 rounded-[10px] p-3 flex flex-col gap-2">
      {/* Header */}
      <div className="flex items-center gap-1.5 mb-1">
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot}`} />
        <span className={`text-[11px] font-bold tracking-widest uppercase ${label}`}>
          {stage}
        </span>
        <span className="ml-auto text-[11px] font-semibold text-stone-400 dark:text-gray-500">
          {applications.length}
        </span>
      </div>

      {/* Cards */}
      {visible.map((app) => (
        <FunnelCard key={app.id} application={app} jobId={jobId} onOpen={onOpen} />
      ))}

      {/* Empty placeholder */}
      {applications.length === 0 && (
        <div className="min-h-[80px] border border-dashed border-stone-200 dark:border-gray-700 rounded-lg" />
      )}

      {/* Overflow */}
      {overflow > 0 && (
        <div className="text-[11px] text-center text-stone-400 dark:text-gray-500 pt-1">
          +{overflow} more
        </div>
      )}
    </div>
  );
}
